import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout, { PageHeader } from "../../components/Layout";
import { api } from "../../lib/api";
import { useStore } from "../../context/StoreContext";
import {
  Gear, Storefront, Users, Printer, CreditCard, ChatCircleText,
  EnvelopeSimple, Cloud, MapPin, FileText, Database, ListChecks,
  CalendarCheck, ArrowRight, CheckCircle, WarningCircle, Circle, ArrowsClockwise, ChartLineUp,
} from "@phosphor-icons/react";

// ─────────────────────────────────────────────────────────────────────────
// Settings hub — flat card grid + category filter, like the screenshot
// reference the merchant supplied. Each tile deep-links to a dedicated
// sub-route under /settings/*. The status pill on each tile is one of:
//   configured    → green   (everything set up)
//   attention     → amber   (key present, but config incomplete)
//   unset         → grey    (no key, integration disabled)
// Integration health for Twilio/Resend/PrintNode/Google comes from
// GET /api/integrations/status (env-var truthiness on the server).
// Tyro health is per-store and read from the StoreContext.
// ─────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: "all",        label: "All settings" },
  { id: "setup",      label: "Setup" },
  { id: "payments",   label: "Payments" },
  { id: "messaging",  label: "Messaging" },
  { id: "devices",    label: "Devices" },
  { id: "data",       label: "Data" },
  { id: "system",     label: "System" },
];

const STATUS_STYLES = {
  configured: { label: "Configured",      cls: "text-[#0F9D58] bg-[#DCFCE7] ring-[#86EFAC]", Icon: CheckCircle },
  attention:  { label: "Needs attention", cls: "text-[#B45309] bg-[#FEF3C7] ring-[#FCD34D]", Icon: WarningCircle },
  unset:      { label: "Not set",         cls: "text-[#6B7280] bg-[#F3F4F6] ring-[#E5E7EB]", Icon: Circle },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.unset;
  const Icon = s.Icon;
  return (
    <span
      data-testid={`tile-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ring-inset ${s.cls}`}
    >
      <Icon size={10} weight="fill" />
      {s.label}
    </span>
  );
}

function Tile({ to, icon, color, title, description, status, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative flex flex-col rounded-2xl border border-[#E5E7EB] bg-white p-5 hover:border-[#121216] hover:shadow-[0_6px_24px_-12px_rgba(0,0,0,0.18)] transition-all"
    >
      <div className="flex items-start justify-between">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: color.bg }}
        >
          {icon}
        </div>
        <StatusPill status={status} />
      </div>
      <div className="mt-4 font-display text-[16px] font-semibold tracking-tight text-[#121216]">
        {title}
      </div>
      <p className="mt-1 text-[12px] text-[#4B5563] leading-relaxed flex-1">
        {description}
      </p>
      <div className="mt-4 flex items-center gap-1.5 text-[11px] font-semibold text-[#1E5FEB] opacity-0 group-hover:opacity-100 transition-opacity">
        Configure
        <ArrowRight size={11} weight="bold" />
      </div>
    </Link>
  );
}

export default function SettingsHub() {
  const { currentStore } = useStore();
  const [cat, setCat] = useState("all");
  const [statuses, setStatuses] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/integrations/status").then((r) => setStatuses(r.data)).catch(() => setStatuses({}));
  }, []);

  // Tyro status comes from the active store config — derived client-side so
  // re-pair / disable changes reflect instantly without re-fetching.
  const tyroStatus = useMemo(() => {
    if (!currentStore) return "unset";
    const enabled = !!currentStore.tyro_enabled;
    const paired  = !!currentStore.tyro_integration_key;
    const mid     = !!currentStore.tyro_merchant_id;
    const tid     = !!currentStore.tyro_terminal_id;
    if (!enabled && !mid && !tid && !paired) return "unset";
    if (enabled && paired && mid && tid)     return "configured";
    return "attention";
  }, [currentStore]);

  const sFor = (k) => statuses?.[k]?.status || "unset";

  const tiles = [
    // Setup
    {
      id: "stores", category: "setup", to: "/settings/stores",
      title: "Stores", description: "Branches, addresses, Google review links and store-specific branding.",
      status: currentStore ? "configured" : "unset",
      icon: <Storefront size={20} weight="duotone" className="text-[#1E5FEB]" />,
      color: { bg: "#DBEAFE" },
      testid: "settings-tile-stores",
    },
    {
      id: "users", category: "setup", to: "/settings/users",
      title: "Staff & users", description: "Manage logins, roles, store assignments and admin PINs.",
      status: "configured",
      icon: <Users size={20} weight="duotone" className="text-[#7C3AED]" />,
      color: { bg: "#EDE9FE" },
      testid: "settings-tile-users",
    },
    {
      id: "general", category: "setup", to: "/settings/general",
      title: "General", description: "Business name, tax mode, surcharge defaults, loyalty and rounding rules.",
      status: "configured",
      icon: <Gear size={20} weight="duotone" className="text-[#4B5563]" />,
      color: { bg: "#F3F4F6" },
      testid: "settings-tile-general",
    },
    // Payments
    {
      id: "tyro", category: "payments", to: "/settings/tyro",
      title: "Tyro EFTPOS", description: "Pair a Tyro terminal, run the Phase 1 cert harness and configure surcharge.",
      status: tyroStatus,
      icon: <img src="/tyro-logo.gif" alt="Tyro" className="w-7 h-7 object-contain" data-testid="settings-tile-tyro-logo" />,
      color: { bg: "#F8FAFC" },
      testid: "settings-tile-tyro",
    },
    // Messaging
    {
      id: "sms", category: "messaging", to: "/settings/sms",
      title: "SMS · Twilio", description: "Outbound repair & marketing SMS, delivery log and A2P 10DLC compliance.",
      status: sFor("sms"),
      icon: <ChatCircleText size={20} weight="duotone" className="text-[#0F9D58]" />,
      color: { bg: "#DCFCE7" },
      testid: "settings-tile-sms",
    },
    {
      id: "email", category: "messaging", to: "/settings/email",
      title: "Email · Resend", description: "Transactional email (receipts, repair updates) via Resend.",
      status: sFor("email"),
      icon: <EnvelopeSimple size={20} weight="duotone" className="text-[#0E7490]" />,
      color: { bg: "#CFFAFE" },
      testid: "settings-tile-email",
    },
    {
      id: "messages", category: "messaging", to: "/settings/messages",
      title: "SMS & Email templates", description: "Wording for every customer text and email — tickets, invoices, receipts and Stripe links.",
      status: "configured",
      icon: <ChatCircleText size={20} weight="duotone" className="text-[#1E5FEB]" />,
      color: { bg: "#DBEAFE" },
      testid: "settings-tile-messages",
    },
    // Devices
    {
      id: "printers", category: "devices", to: "/settings/printers",
      title: "Printers (local)", description: "Default Epson receipt printer, Dymo label printer and paper sizing.",
      status: "configured",
      icon: <Printer size={20} weight="duotone" className="text-[#121216]" />,
      color: { bg: "#F3F4F6" },
      testid: "settings-tile-printers",
    },
    {
      id: "printnode", category: "devices", to: "/settings/printnode",
      title: "PrintNode (cloud)", description: "Cloud print API key for routing receipts & labels to any networked printer.",
      status: sFor("printnode"),
      icon: <Cloud size={20} weight="duotone" className="text-[#1E5FEB]" />,
      color: { bg: "#DBEAFE" },
      testid: "settings-tile-printnode",
    },
    {
      id: "google", category: "system", to: "/settings/google-places",
      title: "Google Places", description: "Address autocomplete for the booking widget and customer onboarding.",
      status: sFor("google_places"),
      icon: <MapPin size={20} weight="duotone" className="text-[#B45309]" />,
      color: { bg: "#FEF3C7" },
      testid: "settings-tile-google",
    },
    // System / Data
    {
      id: "booking", category: "system", to: "/settings/booking-webhook",
      title: "Booking webhook", description: "Inbound bookings from your website — endpoint, API key, sample payload.",
      status: "configured",
      icon: <CalendarCheck size={20} weight="duotone" className="text-[#1E5FEB]" />,
      color: { bg: "#DBEAFE" },
      testid: "settings-tile-booking",
    },
    {
      id: "website-catalog", category: "system", to: "/settings/website-catalog",
      title: "Website catalog sync", description: "Pull manufacturers, models, repairs & part-option pricing from your website nightly.",
      status: "configured",
      icon: <ArrowsClockwise size={20} weight="duotone" className="text-[#1E5FEB]" />,
      color: { bg: "#DBEAFE" },
      testid: "settings-tile-website-catalog",
    },
    {
      id: "templates", category: "system", to: "/settings/templates",
      title: "Templates", description: "Reusable SMS, email and receipt copy with merge tags.",
      status: "configured",
      icon: <FileText size={20} weight="duotone" className="text-[#4B5563]" />,
      color: { bg: "#F3F4F6" },
      testid: "settings-tile-templates",
    },
    {
      id: "data", category: "data", to: "/settings/data",
      title: "Data", description: "Import customers / tickets / invoices and export your full database.",
      status: "configured",
      icon: <Database size={20} weight="duotone" className="text-[#2F8F6E]" />,
      color: { bg: "#DCFCE7" },
      testid: "settings-tile-data",
    },
    {
      id: "migrate", category: "data", to: "/settings/migrate",
      title: "Migrate from POS", description: "One-time bulk migration from RepairDesk / RepairShopr exports.",
      status: "configured",
      icon: <ArrowsClockwise size={20} weight="duotone" className="text-[#2F8F6E]" />,
      color: { bg: "#DCFCE7" },
      testid: "settings-tile-migrate",
    },
    {
      id: "gross", category: "data", to: "/settings/historical-gross",
      title: "Historical revenue", description: "Import RepairDesk multi-store summary so Reports show true history.",
      status: "configured",
      icon: <ChartLineUp size={20} weight="duotone" className="text-[#B45309]" />,
      color: { bg: "#FEF3C7" },
      testid: "settings-tile-gross",
    },
    {
      id: "audit", category: "system", to: "/settings/audit",
      title: "Audit log", description: "Every destructive admin action, void, refund and config change.",
      status: "configured",
      icon: <ListChecks size={20} weight="duotone" className="text-[#4B5563]" />,
      color: { bg: "#F3F4F6" },
      testid: "settings-tile-audit",
    },
  ];

  const filtered = tiles.filter((t) => {
    const okCat = cat === "all" ? true : t.category === cat;
    const q = query.trim().toLowerCase();
    const okQ = !q || t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
    return okCat && okQ;
  });

  return (
    <Layout>
      <PageHeader overline="SETTINGS" title="Integrations & system" />

      <div className="px-8 pt-6 pb-3 bg-white border-b border-[#E5E7EB]">
        <div className="flex flex-wrap items-center gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`settings-cat-${c.id}`}
              className={`h-9 px-3.5 rounded-full text-[12px] font-semibold border transition-colors ${
                cat === c.id
                  ? "bg-[#121216] text-white border-[#121216]"
                  : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#121216] hover:text-[#121216]"
              }`}
            >
              {c.label}
            </button>
          ))}
          <div className="ml-auto">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search settings…"
              data-testid="settings-search"
              className="h-9 w-64 px-3 rounded-full border border-[#E5E7EB] focus:border-[#121216] focus:outline-none text-[12px] bg-white"
            />
          </div>
        </div>
      </div>

      <div className="p-8 bg-[#FAFAFA] min-h-screen">
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
          data-testid="settings-grid"
        >
          {filtered.map((t) => (
            <Tile
              key={t.id}
              to={t.to}
              icon={t.icon}
              color={t.color}
              title={t.title}
              description={t.description}
              status={t.status}
              testid={t.testid}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#E5E7EB] bg-white py-16 text-center text-[12px] text-[#6B7280]">
            No settings match “{query}”.
          </div>
        )}
      </div>
    </Layout>
  );
}
